/**
 * Cart drawer feature module (not a single god-class).
 *
 * These pieces belong to the same cart-drawer feature, but they are separate
 * because they have different DOM lifecycles and responsibilities:
 *
 * - CartDrawer          → <cart-drawer> shell (open / close / focus)
 * - CartDrawerItems     → <cart-drawer-items>, must extend theme CartItems and
 *                         is re-created whenever drawer HTML is re-rendered
 * - CartUpgradeCard     → one <cart-upgrade-card> instance per upgrade CTA;
 *                         mounts/unmounts with line-item HTML
 * - CartGwp             → background cart sync (no custom element); listens to
 *                         cartUpdate and add/removes gift lines from config
 *
 * Keeping them separate avoids a god-object, matches Dawn’s custom-element
 * pattern, and lets re-rendered markup reconnect cleanly. Coordination happens
 * via PUB_SUB_EVENTS.cartUpdate + drawer section refresh — not inheritance.
 */
function currentDrawerSectionId() {
  const drawer = document.querySelector('cart-drawer');
  return drawer ? drawer.dataset.id : '';
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('#cart-icon-bubble, [data-open-cart]').forEach((cartLink) => {
      if (cartLink.dataset.cartBound === 'true') return;
      cartLink.dataset.cartBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const sections = parsedState && parsedState.sections;
    const missingSection = !sections || this.getSectionsToRender().some((section) => !sections[section.id]);
    if (missingSection) {
      this.replaceFromCurrentSection().finally(() => this.finishOpen());
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = this.getSectionInnerHTML(sections[section.id], section.selector);

      if (!sectionElement || html == null) return;
      sectionElement.innerHTML = html;
    });

    this.finishOpen();
  }

  finishOpen() {
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  replaceFromCurrentSection() {
    const drawerSectionId = this.dataset.id || currentDrawerSectionId();
    if (!drawerSectionId) return Promise.resolve();

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}?section_id=${drawerSectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newDrawer = html.querySelector('cart-drawer');
        if (newDrawer) this.innerHTML = newDrawer.innerHTML;
      })
      .then(() => fetch(`${cartUrl}?section_id=cart-icon-bubble`))
      .then((response) => (response ? response.text() : null))
      .then((iconText) => {
        if (!iconText) return;
        const iconHtml = new DOMParser().parseFromString(iconText, 'text/html');
        const source = iconHtml.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: this.dataset.id || currentDrawerSectionId(),
        selector: 'cart-drawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: currentDrawerSectionId(),
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpgradeCard extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.cart-upgrade-card__cta');
    if (!this.button) return;
    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  cartConfig() {
    return typeof fetchConfig === 'function'
      ? fetchConfig()
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };
  }

  cartErrorMessage(data, fallback) {
    if (!data || typeof data !== 'object') return fallback;
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.errors === 'string' && data.errors) return data.errors;
    return fallback;
  }

  isCartError(response, data) {
    if (!response.ok || !data || typeof data !== 'object') return true;
    if (data.errors) return true;
    if (data.status && !Array.isArray(data.items)) return true;
    return false;
  }

  fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}.js`, { credentials: 'same-origin' }).then((response) => response.json());
  }

  sellingPlanId(item) {
    if (!item || !item.selling_plan_allocation) return '';
    const allocation = item.selling_plan_allocation;
    if (allocation.selling_plan && allocation.selling_plan.id) return String(allocation.selling_plan.id);
    if (allocation.selling_plan_id) return String(allocation.selling_plan_id);
    return '';
  }

  async removeOriginalLine(changeUrl, config, predicate) {
    let lastError = null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const cart = await this.fetchCart();
      const original = (cart.items || []).find(predicate);
      if (!original) return cart;

      const response = await fetch(changeUrl, {
        ...config,
        body: JSON.stringify({ id: original.key, quantity: 0 }),
      });
      const data = await response.json();
      if (!this.isCartError(response, data)) return data;

      lastError = new Error(this.cartErrorMessage(data, 'Could not remove original item'));
    }

    throw lastError;
  }

  async onClick(event) {
    event.preventDefault();
    if (this.button.disabled || this.isUpgrading) return;

    const lineKey = this.dataset.upgradeLineKey;
    const fromVariant = this.dataset.upgradeFromVariant;
    const fromSellingPlan = this.dataset.upgradeFromSellingPlan || '';
    const toVariant = this.dataset.upgradeToVariant;
    const sellingPlan = this.dataset.upgradeToSellingPlan;
    const quantity = parseInt(this.dataset.upgradeQuantity || '1', 10) || 1;

    if (!lineKey || !fromVariant || !toVariant || !sellingPlan) return;

    this.isUpgrading = true;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.classList.add('is-loading');

    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const config = this.cartConfig();

    try {
      // Add upgraded item first so a failed add never drops the current line.
      const addResponse = await fetch(addUrl, {
        ...config,
        body: JSON.stringify({
          items: [
            {
              id: Number(toVariant),
              quantity,
              selling_plan: Number(sellingPlan),
            },
          ],
        }),
      });
      const addData = await addResponse.json();
      if (this.isCartError(addResponse, addData)) {
        throw new Error(this.cartErrorMessage(addData, 'Could not add upgraded item'));
      }

      const addedKeys = new Set((addData.items || []).map((item) => item.key).filter(Boolean));
      // Adding a line can rotate the original item's key (discounts, GWP).
      // Re-read the cart and remove the current key, not the one rendered in the drawer.
      const removeData = await this.removeOriginalLine(changeUrl, config, (item) => {
        if (item.key === lineKey) return true;
        if (addedKeys.has(item.key)) return false;
        if (String(item.variant_id) !== String(fromVariant)) return false;
        return this.sellingPlanId(item) === String(fromSellingPlan);
      });

      await this.refreshCartDrawer(removeData);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upgrade-card',
          cartData: removeData,
          variantId: toVariant,
        });
      }
    } catch (error) {
      console.error('[cart-upgrade-card]', error);
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) {
        errors.textContent =
          error.message || (window.cartStrings && window.cartStrings.error) || 'Error';
      }
      this.button.disabled = false;
    } finally {
      this.isUpgrading = false;
      this.button.removeAttribute('aria-busy');
      this.classList.remove('is-loading');
    }
  }

  refreshCartDrawer(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerSectionId = cartDrawer && cartDrawer.dataset.id;
    if (!drawerSectionId) return Promise.resolve();

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(`${cartUrl}?section_id=${drawerSectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newInner = html.querySelector('cart-drawer .drawer__inner');
        const currentInner = document.querySelector('cart-drawer .drawer__inner');
        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        const isEmpty = cartData
          ? cartData.item_count === 0
          : !document.querySelector('cart-drawer .cart-drawer-item');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);

        return fetch(`${cartUrl}?section_id=cart-icon-bubble`);
      })
      .then((response) => (response ? response.text() : null))
      .then((iconText) => {
        if (!iconText) return;
        const iconHtml = new DOMParser().parseFromString(iconText, 'text/html');
        const source = iconHtml.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      });
  }
}

customElements.define('cart-upgrade-card', CartUpgradeCard);

const CartGwp = {
  SOURCE: 'cart-gwp',
  PROPERTY: '_gwp',
  CHECKOUT_LEAVE_KEY: 'noble-checkout-leave',
  syncing: false,
  queued: false,

  setCheckoutLock(locked) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-gwp-syncing', locked);

    const checkoutButtons = document.querySelectorAll(
      '#CartDrawer-Checkout, .cart-drawer__checkout-btn, .onward-checkout-btn, .onward-opt-out-link'
    );
    checkoutButtons.forEach((checkoutBtn) => {
      if (locked) {
        checkoutBtn.disabled = true;
        checkoutBtn.setAttribute('aria-busy', 'true');
        checkoutBtn.setAttribute('aria-disabled', 'true');
        return;
      }

      checkoutBtn.removeAttribute('aria-busy');
      checkoutBtn.removeAttribute('aria-disabled');
      const isEmpty = drawer && drawer.classList.contains('is-empty');
      checkoutBtn.disabled = Boolean(isEmpty) && checkoutBtn.id === 'CartDrawer-Checkout';
    });
  },

  unlockAfterResume() {
    this.syncing = false;
    this.queued = false;
    this.setCheckoutLock(false);
  },

  markCheckoutLeave() {
    try {
      sessionStorage.setItem(this.CHECKOUT_LEAVE_KEY, '1');
    } catch (error) {
      /* ignore */
    }
  },

  consumeCheckoutLeave() {
    try {
      const fromCheckout = sessionStorage.getItem(this.CHECKOUT_LEAVE_KEY) === '1';
      if (fromCheckout) sessionStorage.removeItem(this.CHECKOUT_LEAVE_KEY);
      return fromCheckout;
    } catch (error) {
      return false;
    }
  },

  isBackForwardNavigation() {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward') return true;
    return typeof performance.navigation !== 'undefined' && performance.navigation.type === 2;
  },

  getRules() {
    const el = document.getElementById('CartDrawer-GwpConfig');
    if (!el) return [];
    try {
      const rules = JSON.parse(el.textContent || '[]');
      return Array.isArray(rules)
        ? rules.filter(
            (rule) =>
              rule &&
              Array.isArray(rule.sellingPlanIds) &&
              rule.sellingPlanIds.length > 0 &&
              Array.isArray(rule.variantIds) &&
              rule.variantIds.length > 0
          )
        : [];
    } catch (error) {
      console.error('[cart-gwp] invalid config', error);
      return [];
    }
  },

  getSellingPlanId(item) {
    if (!item) return null;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      return String(item.selling_plan_allocation.selling_plan.id);
    }
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan_id) {
      return String(item.selling_plan_allocation.selling_plan_id);
    }
    return null;
  },

  isGwpLine(item, ruleId) {
    if (!item || !item.properties) return false;
    return String(item.properties[this.PROPERTY] || '') === String(ruleId);
  },

  cartHasSellingPlan(cart, sellingPlanIds) {
    const allowed = new Set(sellingPlanIds.map(String));
    return (cart.items || []).some((item) => {
      if (item.properties && item.properties[this.PROPERTY]) return false;
      const planId = this.getSellingPlanId(item);
      return planId && allowed.has(planId);
    });
  },

  fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}.js`, { credentials: 'same-origin' }).then((response) => response.json());
  },

  fetchConfig() {
    return typeof fetchConfig === 'function'
      ? fetchConfig()
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };
  },

  async applyUpdates(updates) {
    const keys = Object.keys(updates);
    if (!keys.length) return null;

    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    const response = await fetch(updateUrl, {
      ...this.fetchConfig(),
      body: JSON.stringify({ updates }),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Could not update GWP items');
    }
    return data;
  },

  async addItems(items) {
    if (!items.length) return null;

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const response = await fetch(addUrl, {
      ...this.fetchConfig(),
      body: JSON.stringify({ items }),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Could not add GWP items');
    }
    return data;
  },

  refreshCartDrawer(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerSectionId = cartDrawer && cartDrawer.dataset.id;
    if (!drawerSectionId) return Promise.resolve(cartData);

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(`${cartUrl}?section_id=${drawerSectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newInner = html.querySelector('cart-drawer .drawer__inner');
        const currentInner = document.querySelector('cart-drawer .drawer__inner');
        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }

        const isEmpty = cartData
          ? cartData.item_count === 0
          : !document.querySelector('cart-drawer .cart-drawer-item');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);

        return fetch(`${cartUrl}?section_id=cart-icon-bubble`);
      })
      .then((response) => (response ? response.text() : null))
      .then((iconText) => {
        if (!iconText) return cartData;
        const iconHtml = new DOMParser().parseFromString(iconText, 'text/html');
        const source = iconHtml.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
        return cartData;
      });
  },

  async sync(cartData) {
    const rules = this.getRules();
    if (!rules.length) return;

    if (this.syncing) {
      this.queued = true;
      this.setCheckoutLock(true);
      return;
    }

    this.syncing = true;
    this.setCheckoutLock(true);

    try {
      let cart = cartData && cartData.items ? cartData : await this.fetchCart();
      let changed = false;

      for (const rule of rules) {
        const qualified = this.cartHasSellingPlan(cart, rule.sellingPlanIds);
        const updates = {};
        const toAdd = [];

        for (const item of cart.items || []) {
          if (!this.isGwpLine(item, rule.id)) continue;

          if (!qualified) {
            updates[item.key] = 0;
            continue;
          }

          const variantId = Number(item.variant_id || item.id);
          if (!rule.variantIds.map(Number).includes(variantId)) {
            updates[item.key] = 0;
            continue;
          }

          if (item.quantity !== 1) {
            updates[item.key] = 1;
          }
        }

        if (qualified) {
          rule.variantIds.forEach((variantId, orderIndex) => {
            const id = Number(variantId);
            const existing = (cart.items || []).find(
              (item) => this.isGwpLine(item, rule.id) && Number(item.variant_id || item.id) === id
            );
            if (!existing) {
              toAdd.push({
                id,
                quantity: 1,
                properties: {
                  [this.PROPERTY]: String(rule.id),
                  _gwp_order: String(orderIndex + 1),
                },
              });
            }
          });
        }

        if (Object.keys(updates).length) {
          cart = (await this.applyUpdates(updates)) || cart;
          changed = true;
        }

        if (toAdd.length) {
          await this.addItems(toAdd);
          cart = await this.fetchCart();
          changed = true;
        }
      }

      if (changed) {
        cart = cart && cart.items ? cart : await this.fetchCart();
        await this.refreshCartDrawer(cart);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: this.SOURCE,
            cartData: cart,
          });
        }
      }
    } catch (error) {
      console.error('[cart-gwp]', error);
    } finally {
      this.syncing = false;
      if (this.queued) {
        this.queued = false;
        this.sync();
      } else {
        this.setCheckoutLock(false);
      }
    }
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === this.SOURCE) return;
        this.sync(event.cartData);
      });
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('#CartDrawer-Checkout, .onward-checkout-btn, [name="checkout"]')) return;
        this.markCheckoutLeave();
      },
      true
    );

    window.addEventListener('pagehide', () => {
      const leavingToCheckout = Boolean(
        document.querySelector(
          'cart-drawer.is-gwp-syncing, .onward-checkout-btn[disabled], .onward-opt-out-link[disabled], .onward-checkout-btn[aria-busy="true"]'
        )
      );
      if (leavingToCheckout) this.markCheckoutLeave();
    });

    window.addEventListener('pageshow', (event) => {
      const fromCheckout = this.consumeCheckoutLeave();
      const backForward = Boolean(event.persisted) || this.isBackForwardNavigation();

      this.unlockAfterResume();

      if (backForward && fromCheckout) {
        window.location.reload();
        return;
      }

      if (backForward) this.sync();
    });

    this.sync();
  },
};

CartGwp.init();

/* Theme editor: open drawer when Cart Drawer section/block is selected */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  const matchDrawer = (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    const id = event.detail?.sectionId;
    if (id && String(drawer.dataset.id) === String(id)) return drawer;
    if (event.target?.closest?.('cart-drawer')) return drawer;
    if (id && document.getElementById(`shopify-section-${id}`)?.querySelector('cart-drawer')) return drawer;
    return null;
  };

  document.addEventListener('shopify:section:select', (e) => matchDrawer(e)?.open());
  document.addEventListener('shopify:block:select', (e) => matchDrawer(e)?.open());
  document.addEventListener('shopify:section:deselect', (e) => matchDrawer(e)?.close());
  document.addEventListener('shopify:section:load', (e) => {
    const drawer = matchDrawer(e);
    if (drawer) requestAnimationFrame(() => drawer.open());
  });
}
